import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { pointerX, pointerY } from "../lib/parallax";
import ShaderFX from "./ShaderFX";

const SPRING = { stiffness: 50, damping: 18, mass: 0.6 };

// Pointer-driven displacement (px). Positive strength follows the cursor,
// negative moves against it — layering different strengths creates depth.
export function usePointer(strength) {
  const reduced = useReducedMotion();
  const x = useSpring(useTransform(pointerX, (v) => (reduced ? 0 : v * strength)), SPRING);
  const y = useSpring(useTransform(pointerY, (v) => (reduced ? 0 : v * strength)), SPRING);
  return { x, y };
}

// Oversized, cursor-reactive WebGL section background.
export function ParallaxShader({ variant = "ink", intensity = 0.9, strength = 28 }) {
  const { x, y } = usePointer(strength);
  return (
    <motion.div style={{ x, y }} className="pointer-events-none absolute -inset-[10%] z-0">
      <ShaderFX variant={variant} intensity={intensity} className="h-full w-full" />
    </motion.div>
  );
}

// Generic parallax layer (glows, kanji watermarks, text blocks).
export function ParallaxSpan({ children, className = "", style, strength = 50 }) {
  const { x, y } = usePointer(strength);
  return (
    <motion.span aria-hidden="true" style={{ x, y, ...style }} className={className}>
      {children}
    </motion.span>
  );
}

export function PointerLayer({ children, strength = 60, className = "", style }) {
  const { x, y } = usePointer(strength);
  return (
    <motion.div style={{ x, y, ...style }} className={className}>
      {children}
    </motion.div>
  );
}

// Cursor-following 3D tilt for cards (depth on hover).
export function TiltCard({ children, className = "", max = 6 }) {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 15 });
  const sry = useSpring(ry, { stiffness: 150, damping: 15 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * max * 2);
    rx.set(-ny * max * 2);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div className={`[perspective:1200px] ${className}`}>
      <motion.div
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="h-full [transform-style:preserve-3d]"
      >
        {children}
      </motion.div>
    </div>
  );
}
